import { Button, FormLabel, FormMessage, Input } from "@/atoms";
import { WrappedMarkdownInput } from "@/forms/markdowninput";
import ListSelector from "@/forms/selectors/listselector";
import { Description } from "@/globals";
import H2 from "@/headings/h2";
import GlobalErrorPopout from "@/notifications/globalerror";
import TargetsList, { targetSchema } from "@/reports/findings/targetslist";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import invariant from "tiny-invariant";
import z from "zod";

import type {
  CreateFindingRequest,
  Finding,
  TenantTester,
  UpdateFindingRequest,
} from "@oneleet/api_client";
import {
  ClassificationImpact,
  ClassificationProbability,
  ClassificationRisk,
  FindingKind,
  FindingPriority,
  FindingState,
  TargetKind,
  queries,
} from "@oneleet/api_client";
import api from "@oneleet/api_client";
import { getFindingUrl } from "@oneleet/utils/src/urls";
import { getDirtyValues as dirtyValues } from "@oneleet/utils/src/utils";

type Props = {
  // If a finding is passed in, this will call an update. Otherwise, it will create a new finding.
  finding?: Finding;
  admin?: {
    testers: Array<TenantTester>;
  };

  // testerId is used to pre-select a tester in the form
  // or to create a finding for the tester that is currently logged in
  // The API will validate that the testerId is valid
  testerId?: string;
};

const READABLE_ID_MAX_LENGTH = 24;

const createFindingForm = z.object({
  testerId: z.string().uuid(),
  title: z.string().min(1, "Title cannot be empty"),
  readableId: z
    .string()
    .min(1, "Readable ID cannot be empty")
    .max(
      READABLE_ID_MAX_LENGTH,
      `Readable ID is too long. Max ${READABLE_ID_MAX_LENGTH} characters.`,
    ),
  state: z.nativeEnum(FindingState).optional(),

  kind: z.nativeEnum(FindingKind, {
    required_error: "You need to pick a finding kind",
  }),
  priority: z.nativeEnum(FindingPriority).optional(),

  impact: z.nativeEnum(ClassificationImpact),
  probability: z.nativeEnum(ClassificationProbability),
  risk: z.nativeEnum(ClassificationRisk),
  targets: z
    .array(targetSchema)
    .optional()
    .refine(
      (targets) => {
        if (!targets) {
          return true;
        }

        if (targets.length > 0) {
          return targets.every((target) => {
            return target.kind === TargetKind.FILEPATH
              ? target.filepath
              : target.hostPort;
          });
        }

        return true;
      },
      {
        message: "All targets must have a value",
      },
    ),
  specificDescription: z.string().optional(),
  generalDescription: z.string(),
  reproductionSteps: z.string().optional(),
  businessImpact: z.string().optional(),
  remediation: z.string().optional(),
});

type CreateFindingForm = z.infer<typeof createFindingForm>;

const CreateFinding = ({ admin, testerId }: Props) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<CreateFindingForm>({
    defaultValues: {
      state: FindingState.DRAFT,
      testerId: testerId,
    },
    resolver: zodResolver(createFindingForm),
  });

  const [unknownError, setUnknownError] = useState<string | null>(null);
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const createFinding = useMutation({
    mutationKey: ["finding_create"],
    mutationFn: async (newFinding: CreateFindingRequest) => {
      setUnknownError(null);

      invariant(params.report);

      const res = await api.browser.findingCreate(params.report, newFinding);

      return res;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: queries.report.get(params.report).queryKey,
      });
      void navigate(getFindingUrl(data.data.id, params));
    },
    onError: async (error) => {
      if (typeof error === "object" && error !== null && "error" in error) {
        if (
          typeof error.error === "object" &&
          error.error !== null &&
          "message" in error.error &&
          typeof error.error.message === "string"
        ) {
          setUnknownError(error.error.message);
          return;
        }
      }

      if (error instanceof Request) {
        const errBody: unknown = await error.json();

        if (
          typeof errBody === "object" &&
          errBody !== null &&
          "message" in errBody &&
          typeof errBody.message === "string"
        ) {
          setUnknownError(errBody.message);
        }
      }
    },
  });

  const kindWatcher = watch("kind");

  useEffect(() => {
    if (kindWatcher === FindingKind.INFORMATIONAL) {
      setValue("impact", ClassificationImpact.NOTAVAILABLE);
      setValue("probability", ClassificationProbability.NOTAVAILABLE);
      setValue("risk", ClassificationRisk.NOTE);
    }
  }, [kindWatcher, setValue]);

  const impactWatcher = watch("impact");
  const probabilityWatcher = watch("probability");
  const riskWatcher = watch("risk");

  useEffect(() => {
    if (impactWatcher && probabilityWatcher && !riskWatcher) {
      setValue(
        "risk",
        getRiskFromImpactAndProbability(impactWatcher, probabilityWatcher),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskWatcher, impactWatcher, probabilityWatcher]);

  return (
    <>
      {unknownError && (
        <GlobalErrorPopout
          title="Error creating finding"
          description={unknownError || ""}
        />
      )}
      <form
        onSubmit={handleSubmit((data) => {
          createFinding.mutate({
            ...data,
            // Priority should be deprecated but the API still requires it
            priority: getPriorityFromRisk(data.risk),
          });
        })}
      >
        <div className="mt-2 flex flex-col gap-2 px-4 pb-8 sm:px-8">
          {admin ? (
            <>
              <h1 className="text-lg font-semibold">Create finding</h1>
              <Controller
                name="testerId"
                control={control}
                render={({ field }) => {
                  return (
                    <div className="mt-4">
                      <ListSelector
                        value={field.value || ""}
                        onChange={(t) => field.onChange(t)}
                        label="Assign a tester *"
                        placeholder="Tester"
                        options={admin.testers.map((tester) => {
                          return {
                            label: tester.user?.email || "No email",
                            value: tester.id || "",
                          };
                        })}
                      />
                      {errors.testerId ? (
                        <FormMessage
                          text={errors.testerId?.message || ""}
                          color="red"
                        />
                      ) : null}
                    </div>
                  );
                }}
              />
            </>
          ) : null}

          <H2 title="Finding Overview" className="mt-6" />

          <div className="flex flex-col gap-6">
            <Controller
              name="state"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <div>
                    <ListSelector
                      value={field.value || ""}
                      onChange={(t) => field.onChange(t)}
                      label="Set the initial finding state"
                      placeholder="State"
                      options={[
                        { label: "Draft", value: FindingState.DRAFT },
                        { label: "Open", value: FindingState.OPEN },
                        {
                          label: "Remediated",
                          value: FindingState.REMEDIATED,
                        },
                      ]}
                    />
                    {fieldState.error ? (
                      <FormMessage
                        text={fieldState.error?.message || ""}
                        color="red"
                      />
                    ) : null}
                  </div>
                );
              }}
            />
            <div>
              <FormLabel text="Give this finding a descriptive title * " />
              <Input {...register("title")} className="mt-2" />
              {errors.title ? (
                <FormMessage text={errors.title?.message || ""} color="red" />
              ) : null}
            </div>

            <div>
              <FormLabel text="Give this finding a readable ID *" />
              <Input
                {...register("readableId")}
                placeholder="ONE-001"
                className="mt-2"
              />
              {errors.readableId ? (
                <FormMessage
                  text={errors.readableId.message || ""}
                  color="red"
                />
              ) : null}
            </div>

            <Controller
              name="kind"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <div>
                    <ListSelector
                      value={field.value}
                      onChange={(t) => field.onChange(t)}
                      label="Select a finding kind *"
                      placeholder="Kind"
                      options={[
                        {
                          label: "Vulnerability",
                          value: FindingKind.VULNERABILITY,
                        },
                        {
                          label: "Informational",
                          value: FindingKind.INFORMATIONAL,
                        },
                      ]}
                    />
                    {fieldState.error ? (
                      <FormMessage
                        text={fieldState.error?.message || ""}
                        color="red"
                      />
                    ) : null}
                  </div>
                );
              }}
            />

            <Controller
              name="targets"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <TargetsList
                    value={field.value || []}
                    onChange={(t) => field.onChange(t)}
                    error={fieldState.error?.message || ""}
                    className="mt-4"
                  />
                );
              }}
            />
          </div>

          <div className="my-6 flex flex-col gap-2">
            <H2 title="Classification" />
            <Description>
              Give this finding an impact and probability to compute a risk
              score.
            </Description>

            <div className="flex flex-col gap-6">
              <Controller
                name="impact"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <div>
                      <ListSelector
                        value={field.value}
                        onChange={(t) => field.onChange(t)}
                        label="Select a finding impact *"
                        placeholder="Impact"
                        options={[
                          { label: "High", value: "HIGH" },
                          { label: "Medium", value: "MEDIUM" },
                          { label: "Low", value: "LOW" },
                          { label: "N/A", value: "NOTAVAILABLE" },
                        ]}
                      />
                      {fieldState.error ? (
                        <FormMessage
                          text={fieldState.error?.message || ""}
                          color="red"
                        />
                      ) : null}
                    </div>
                  );
                }}
              />

              <Controller
                name="probability"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <div>
                      <ListSelector
                        value={field.value}
                        onChange={(t) => field.onChange(t)}
                        name="probability"
                        label="Select a finding probability *"
                        placeholder="Probability"
                        options={[
                          { label: "High", value: "HIGH" },
                          { label: "Medium", value: "MEDIUM" },
                          { label: "Low", value: "LOW" },
                          { label: "N/A", value: "NOTAVAILABLE" },
                        ]}
                      />
                      {fieldState.error ? (
                        <FormMessage
                          text={fieldState.error?.message || ""}
                          color="red"
                        />
                      ) : null}
                    </div>
                  );
                }}
              />

              <Controller
                name="risk"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <div>
                      <ListSelector
                        value={field.value}
                        onChange={(t) => field.onChange(t)}
                        label="Select a finding risk (this is automatically computed from the impact and probability, but it can be overriden) *"
                        placeholder="Risk"
                        options={[
                          { label: "Critical", value: "CRITICAL" },
                          { label: "High", value: "HIGH" },
                          { label: "Medium", value: "MEDIUM" },
                          { label: "Low", value: "LOW" },
                          { label: "Note", value: "NOTE" },
                        ]}
                      />
                      {fieldState.error ? (
                        <FormMessage
                          text={fieldState.error?.message || ""}
                          color="red"
                        />
                      ) : null}
                    </div>
                  );
                }}
              />
            </div>
          </div>
          <div className="my-6 flex flex-col gap-2">
            <H2 title="Descriptions" />
            <Controller
              name="generalDescription"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <WrappedMarkdownInput
                      name="generalDescription"
                      value={field.value || ""}
                      onChange={(t) => field.onChange(t)}
                      title="Description *"
                    />
                    {fieldState.error ? (
                      <FormMessage
                        text={fieldState.error?.message || ""}
                        color="red"
                      />
                    ) : null}
                  </>
                );
              }}
            />

            <Controller
              name="businessImpact"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <WrappedMarkdownInput
                      name="businessImpact"
                      value={field.value || ""}
                      onChange={(t) => field.onChange(t)}
                      title="Business Impact"
                    />
                    {fieldState.error ? (
                      <FormMessage
                        text={fieldState.error?.message || ""}
                        color="red"
                      />
                    ) : null}
                  </>
                );
              }}
            />
            <Controller
              name="reproductionSteps"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <WrappedMarkdownInput
                      name="reproductionSteps"
                      value={field.value || ""}
                      onChange={(t) => field.onChange(t)}
                      title="How to reproduce"
                    />
                    {fieldState.error ? (
                      <FormMessage
                        text={fieldState.error?.message || ""}
                        color="red"
                      />
                    ) : null}
                  </>
                );
              }}
            />

            <Controller
              name="remediation"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <WrappedMarkdownInput
                      error={fieldState.error?.message}
                      name="remediation"
                      value={field.value || ""}
                      onChange={(t) => field.onChange(t)}
                      title="Remediation"
                    />
                    {fieldState.error ? (
                      <FormMessage
                        text={fieldState.error?.message || ""}
                        color="red"
                      />
                    ) : null}
                  </>
                );
              }}
            />
          </div>
          <div className="mt-4 flex flex-row items-center justify-end gap-4">
            <Button
              type="submit"
              color="brand"
              label="Create finding"
              variant="solid"
              isLoading={createFinding.isPending}
            />
          </div>
        </div>
      </form>
    </>
  );
};

const updateFindingForm = createFindingForm.partial();

type UpdateFindingForm = z.infer<typeof updateFindingForm>;

const UpdateFinding = ({ finding, admin, testerId }: Props) => {
  const transformedTargets = useMemo(() => {
    return finding?.targets
      ?.map((target) => {
        if (target.kind === TargetKind.HOSTPORT) {
          return {
            kind: TargetKind.HOSTPORT as const,
            hostPort: target.hostPort || "",
          };
        } else if (target.kind === TargetKind.FILEPATH) {
          return {
            kind: TargetKind.FILEPATH as const,
            filepath: target.filepath || "",
          };
        }
        return undefined;
      })
      .filter((t): t is Exclude<typeof t, undefined> => t !== undefined);
  }, [finding?.targets]);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, dirtyFields },
    watch,
    setValue,
    reset,
  } = useForm<UpdateFindingForm>({
    defaultValues: {
      ...finding,
      targets: transformedTargets,
      impact:
        finding?.classification?.impact || ClassificationImpact.NOTAVAILABLE,
      probability:
        finding?.classification?.probability ||
        ClassificationProbability.NOTAVAILABLE,
      risk: finding?.classification?.risk || ClassificationRisk.NOTE,
      generalDescription: finding?.findingText?.generalDescription || "",
      specificDescription: finding?.findingText?.specificDescription || "",
      reproductionSteps: finding?.findingText?.reproductionSteps || "",
      businessImpact: finding?.findingText?.businessImpact || "",
      remediation: finding?.findingText?.remediation || "",
      testerId: finding?.testerId ?? testerId,
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(updateFindingForm),
  });

  const [unknownError, setUnknownError] = useState<string | null>(null);
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const updateFinding = useMutation({
    mutationKey: ["finding_update"],
    mutationFn: async (updatedFinding: UpdateFindingRequest) => {
      setUnknownError(null);

      invariant(params.finding);

      const res = await api.browser.findingUpdate(
        params.finding,
        updatedFinding,
      );

      return res;
    },
    onSuccess: (data) => {
      reset();
      void queryClient.invalidateQueries({
        queryKey: queries.report.get(params.report).queryKey,
      });
      void navigate(getFindingUrl(data.data.id, params));
    },
    onError: async (error) => {
      if (typeof error === "object" && error !== null && "error" in error) {
        if (
          typeof error.error === "object" &&
          error.error !== null &&
          "message" in error.error &&
          typeof error.error.message === "string"
        ) {
          setUnknownError(error.error.message);
          return;
        }
      }

      if (error instanceof Request) {
        const errBody: unknown = await error.json();

        if (
          typeof errBody === "object" &&
          errBody !== null &&
          "message" in errBody &&
          typeof errBody.message === "string"
        ) {
          setUnknownError(errBody.message);
        }
      }
    },
  });

  const kindWatcher = watch("kind");

  useEffect(() => {
    if (kindWatcher === FindingKind.INFORMATIONAL) {
      setValue("impact", ClassificationImpact.NOTAVAILABLE);
      setValue("probability", ClassificationProbability.NOTAVAILABLE);
      setValue("risk", ClassificationRisk.NOTE);
    }
  }, [kindWatcher, setValue]);

  const impactWatcher = watch("impact");
  const probabilityWatcher = watch("probability");
  const riskWatcher = watch("risk");

  useEffect(() => {
    if (impactWatcher && probabilityWatcher && !riskWatcher) {
      setValue(
        "risk",
        getRiskFromImpactAndProbability(impactWatcher, probabilityWatcher),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskWatcher, impactWatcher, probabilityWatcher]);

  if (!finding) {
    return null;
  }

  const allowedStates = admin
    ? [
        { label: "Draft", value: FindingState.DRAFT },
        { label: "Open", value: FindingState.OPEN },
        { label: "Remediated", value: FindingState.REMEDIATED },
        { label: "In Review", value: FindingState.IN_REVIEW },
        { label: "In Progress", value: FindingState.IN_PROGRESS },
        { label: "Rejected", value: FindingState.REJECTED },
        { label: "Duplicate", value: FindingState.DUPLICATE },
        { label: "False Positive", value: FindingState.FALSE_POSITIVE },
        {
          label: "Accepted Risk",
          value: FindingState.ACCEPTED_RISK,
        },
      ]
    : [
        { label: "Draft", value: FindingState.DRAFT },
        { label: "Open", value: FindingState.OPEN },
        { label: "Remediated", value: FindingState.REMEDIATED },
      ];

  return (
    <>
      {unknownError && (
        <GlobalErrorPopout
          title="Error creating finding"
          description={unknownError || ""}
        />
      )}
      <form
        onSubmit={handleSubmit((data) => {
          // Priority should be deprecated but the API still requires it
          let priority = data.priority;
          if (dirtyFields.risk && data.risk) {
            priority = getPriorityFromRisk(data.risk);
          }

          updateFinding.mutate({
            // @ts-expect-error - data is not typed correctly - if this is broken the codeowner here is either Roman or Nico
            ...(dirtyValues(dirtyFields, data) as UpdateFindingRequest),
            ...(priority != finding.priority && {
              priority,
            }),
          });
        })}
      >
        <div className="mt-2 flex flex-col gap-2 px-4 pb-8 sm:px-8">
          {admin ? (
            <>
              <h1 className="text-lg font-semibold">Update finding</h1>
              <Controller
                name="testerId"
                control={control}
                render={({ field }) => {
                  return (
                    <div className="mt-4">
                      <ListSelector
                        value={field.value || ""}
                        onChange={(t) => field.onChange(t)}
                        label="Assign a tester *"
                        placeholder="Tester"
                        options={admin.testers.map((tester) => {
                          return {
                            label: tester.user?.email || "No email",
                            value: tester.id || "",
                          };
                        })}
                      />
                      {errors.testerId ? (
                        <FormMessage
                          text={errors.testerId?.message || ""}
                          color="red"
                        />
                      ) : null}
                    </div>
                  );
                }}
              />
            </>
          ) : null}

          <div className="mt-6 flex flex-col gap-2">
            <Controller
              name="state"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <ListSelector
                      value={field.value || ""}
                      onChange={(t) => field.onChange(t)}
                      label="Update the finding state"
                      placeholder="State"
                      options={allowedStates}
                    />
                    {fieldState.error ? (
                      <FormMessage
                        text={fieldState.error?.message || ""}
                        color="red"
                      />
                    ) : null}
                  </>
                );
              }}
            />
            <Description>
              Most recent change request: {finding?.reviewReason} (
              {finding?.reviewReasonText})
            </Description>
          </div>

          <H2 title="Finding Overview" className="mt-6" />
          <div className="flex flex-col gap-6">
            <div>
              <FormLabel text="Give this finding a descriptive title *" />
              <Input {...register("title")} />
              {errors.title ? (
                <FormMessage text={errors.title?.message || ""} color="red" />
              ) : null}
            </div>

            <div>
              <FormLabel text="Give this finding a readable ID *" />
              <Input {...register("readableId")} placeholder="ONE-001" />
              {errors.readableId ? (
                <FormMessage
                  text={errors.readableId.message || ""}
                  color="red"
                />
              ) : null}
            </div>

            <Controller
              name="kind"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <div>
                    <ListSelector
                      value={field.value || ""}
                      onChange={(t) => field.onChange(t)}
                      label="Select a finding kind *"
                      placeholder="Kind"
                      options={[
                        {
                          label: "Vulnerability",
                          value: FindingKind.VULNERABILITY,
                        },
                        {
                          label: "Informational",
                          value: FindingKind.INFORMATIONAL,
                        },
                      ]}
                    />
                    {fieldState.error ? (
                      <FormMessage
                        text={fieldState.error?.message || ""}
                        color="red"
                      />
                    ) : null}
                  </div>
                );
              }}
            />

            <Controller
              name="targets"
              control={control}
              render={({ field, fieldState }) => (
                <TargetsList
                  value={field.value || []}
                  onChange={(t) => field.onChange(t)}
                  error={fieldState.error?.message || ""}
                  className="mt-4"
                />
              )}
            />
          </div>

          <div className="my-6 flex flex-col gap-2">
            <H2 title="Classification" />
            <Description>
              Give this finding an impact and probability to compute a risk
              score.
            </Description>

            <div className="flex flex-col gap-6">
              <Controller
                name="impact"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <div>
                      <ListSelector
                        value={field.value || ""}
                        onChange={(t) => field.onChange(t)}
                        label="Select a finding impact *"
                        placeholder="Impact"
                        options={[
                          { label: "High", value: "HIGH" },
                          { label: "Medium", value: "MEDIUM" },
                          { label: "Low", value: "LOW" },
                          { label: "N/A", value: "NOTAVAILABLE" },
                        ]}
                      />
                      {fieldState.error ? (
                        <FormMessage
                          text={fieldState.error?.message || ""}
                          color="red"
                        />
                      ) : null}
                    </div>
                  );
                }}
              />

              <Controller
                name="probability"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <div>
                      <ListSelector
                        value={field.value || ""}
                        onChange={(t) => field.onChange(t)}
                        name="probability"
                        label="Select a finding probability *"
                        placeholder="Probability"
                        options={[
                          { label: "High", value: "HIGH" },
                          { label: "Medium", value: "MEDIUM" },
                          { label: "Low", value: "LOW" },
                          { label: "N/A", value: "NOTAVAILABLE" },
                        ]}
                      />
                      {fieldState.error ? (
                        <FormMessage
                          text={fieldState.error?.message || ""}
                          color="red"
                        />
                      ) : null}
                    </div>
                  );
                }}
              />

              <Controller
                name="risk"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <div>
                      <ListSelector
                        value={field.value || ""}
                        onChange={(t) => field.onChange(t)}
                        label="Select a finding risk (this is automatically computed from the impact and probability, but it can be overriden) *"
                        placeholder="Risk"
                        options={[
                          { label: "Critical", value: "CRITICAL" },
                          { label: "High", value: "HIGH" },
                          { label: "Medium", value: "MEDIUM" },
                          { label: "Low", value: "LOW" },
                          { label: "Note", value: "NOTE" },
                        ]}
                      />
                      {fieldState.error ? (
                        <FormMessage
                          text={fieldState.error?.message || ""}
                          color="red"
                        />
                      ) : null}
                    </div>
                  );
                }}
              />
            </div>
          </div>
          <div className="my-6 flex flex-col gap-2">
            <H2 title="Descriptions" />
            <Controller
              name="generalDescription"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <WrappedMarkdownInput
                      name="generalDescription"
                      value={field.value || ""}
                      onChange={(t) => field.onChange(t)}
                      title="Description *"
                    />
                    {fieldState.error ? (
                      <FormMessage
                        text={fieldState.error?.message || ""}
                        color="red"
                      />
                    ) : null}
                  </>
                );
              }}
            />

            <Controller
              name="businessImpact"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <WrappedMarkdownInput
                      name="businessImpact"
                      value={field.value || ""}
                      onChange={(t) => field.onChange(t)}
                      title="Business Impact"
                    />
                    {fieldState.error ? (
                      <FormMessage
                        text={fieldState.error?.message || ""}
                        color="red"
                      />
                    ) : null}
                  </>
                );
              }}
            />
            <Controller
              name="reproductionSteps"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <WrappedMarkdownInput
                      name="reproductionSteps"
                      value={field.value || ""}
                      onChange={(t) => field.onChange(t)}
                      title="How to reproduce"
                    />
                    {fieldState.error ? (
                      <FormMessage
                        text={fieldState.error?.message || ""}
                        color="red"
                      />
                    ) : null}
                  </>
                );
              }}
            />

            <Controller
              name="remediation"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <WrappedMarkdownInput
                      error={fieldState.error?.message}
                      name="remediation"
                      value={field.value || ""}
                      onChange={(t) => field.onChange(t)}
                      title="Remediation"
                    />
                    {fieldState.error ? (
                      <FormMessage
                        text={fieldState.error?.message || ""}
                        color="red"
                      />
                    ) : null}
                  </>
                );
              }}
            />
          </div>
          <div className="mt-4 flex flex-row items-center justify-end gap-4">
            <Button
              type="submit"
              color="brand"
              label="Update finding"
              variant="solid"
              isLoading={updateFinding.isPending}
            />
          </div>
        </div>
      </form>
    </>
  );
};

export default function CreateOrUpdateFindingComponent({
  finding,
  admin,
  testerId,
}: Props) {
  if (!finding) {
    return <CreateFinding admin={admin} testerId={testerId} />;
  }

  return <UpdateFinding finding={finding} admin={admin} testerId={testerId} />;
}

const getRiskFromImpactAndProbability = (
  impact: ClassificationImpact,
  probability: ClassificationProbability,
) => {
  if (
    impact === ClassificationImpact.NOTAVAILABLE ||
    probability === ClassificationProbability.NOTAVAILABLE
  ) {
    return ClassificationRisk.NOTE;
  }

  if (
    impact === ClassificationImpact.LOW &&
    probability === ClassificationProbability.LOW
  ) {
    return ClassificationRisk.LOW;
  }

  if (
    impact === ClassificationImpact.LOW &&
    probability === ClassificationProbability.MEDIUM
  ) {
    return ClassificationRisk.LOW;
  }

  if (
    impact === ClassificationImpact.LOW &&
    probability === ClassificationProbability.HIGH
  ) {
    return ClassificationRisk.MEDIUM;
  }

  if (
    impact === ClassificationImpact.MEDIUM &&
    probability === ClassificationProbability.LOW
  ) {
    return ClassificationRisk.LOW;
  }

  if (
    impact === ClassificationImpact.MEDIUM &&
    probability === ClassificationProbability.MEDIUM
  ) {
    return ClassificationRisk.MEDIUM;
  }

  if (
    impact === ClassificationImpact.MEDIUM &&
    probability === ClassificationProbability.HIGH
  ) {
    return ClassificationRisk.HIGH;
  }

  if (
    impact === ClassificationImpact.HIGH &&
    probability === ClassificationProbability.LOW
  ) {
    return ClassificationRisk.MEDIUM;
  }

  if (
    impact === ClassificationImpact.HIGH &&
    probability === ClassificationProbability.MEDIUM
  ) {
    return ClassificationRisk.HIGH;
  }

  if (
    impact === ClassificationImpact.HIGH &&
    probability === ClassificationProbability.HIGH
  ) {
    return ClassificationRisk.CRITICAL;
  }

  throw new Error("Invalid impact and probability combination");
};

// eslint-disable-next-line react-refresh/only-export-components
export const getPriorityFromRisk = (
  risk: ClassificationRisk,
): FindingPriority => {
  if (risk === ClassificationRisk.CRITICAL) {
    return FindingPriority.CRITICAL;
  }

  if (risk === ClassificationRisk.HIGH) {
    return FindingPriority.HIGH;
  }

  if (risk === ClassificationRisk.MEDIUM) {
    return FindingPriority.MEDIUM;
  }

  if (risk === ClassificationRisk.LOW) {
    return FindingPriority.LOW;
  }

  return FindingPriority.INFORMATIONAL;
};
